import { AbstractTransport } from './abstract-transport';
import { isBrowser, randomFrom, isIE } from '../utils';
import '../types';

/**
 * Evenly distributes colors among prefixes, when there are no free (unused) colors,
 * picks color for new prefix randomly.
 * @param {string[]} colors
 * @returns {(prefix: string) => string} - function to get color by prefix
 */
export const colorByPrefixGetter = colors => {
    const map = {};
    const primeNumSteps = [13, 11, 7, 5, 3, 2];
    // try not to return colors sequentially, because they may seem similar visually
    const step = primeNumSteps.find(n => colors.length % n !== 0) || 1;
    let curr = step;

    const getUnusedColor = () => {
        const color = colors[curr];
        curr = curr === 0 ? null : (curr + step) % colors.length;
        return color;
    };

    const getColor = () => (curr === null ? randomFrom(colors) : getUnusedColor());

    return prefix => map[prefix] || Object.assign(map, { [prefix]: getColor() })[prefix];
};

export class ConsoleTransport extends AbstractTransport {
    /** @param {{ devToolsTheme: DevToolsTheme }} opts */
    static create(opts) {
        return isBrowser() ? new BrowserConsoleTransport(opts) : new NodeConsoleTransport(opts);
    }
}

export class BrowserConsoleTransport extends ConsoleTransport {
    // prettier-ignore
    static colors = {
        common: ['#05f', '#55f', '#a0f', '#a50', '#a55', '#a5a', '#a5f', '#f00', '#f05', '#f0a'],
        dark: ['#000', '#005', '#00a', '#00f', '#050', '#055', '#05a', '#500', '#505', '#50a', '#50f', '#550', '#555', '#55a', '#a00', '#a05', '#a0a'],
        light: ['#0a0', '#0a5', '#0aa', '#0af', '#0f0', '#0f5', '#0fa', '#0ff', '#5a0', '#5a5', '#5aa', '#5af', '#5f0', '#5f5', '#5fa', '#5ff', '#aa0', '#aa5', '#aaa', '#aaf', '#af0', '#af5', '#afa', '#aff', '#f0f', '#f50', '#f55', '#f5a', '#f5f', '#fa0', '#fa5', '#faa', '#faf', '#ff0', '#ff5', '#ffa', '#fff'],
    };

    /** @param {{ devToolsTheme: DevToolsTheme }} opts */
    constructor({ devToolsTheme } = {}) {
        super();
        this.applyRuntimeOpts({ devToolsTheme });
        this.getColorByPrefix = colorByPrefixGetter(this.getColors(devToolsTheme));
    }

    /**
     * @private
     * @param {DevToolsTheme=} devToolsTheme - devtools color scheme
     * @returns {string[]} list of rgb colors
     */
    getColors(devToolsTheme) {
        const { common, light, dark } = BrowserConsoleTransport.colors;
        switch (devToolsTheme) {
            case 'light':
                return [...dark, ...common];
            case 'dark':
                return [...light, ...common];
            case 'system':
                if (!globalThis.matchMedia) return common;

                // checks system color scheme, not browser dev-tools
                return globalThis.matchMedia('(prefers-color-scheme: dark)').matches
                    ? [...light, ...common]
                    : [...dark, ...common];
            default:
                return common;
        }
    }

    /** @param {{ devToolsTheme: DevToolsTheme }} opts */
    applyRuntimeOpts(opts = {}) {
        if (opts.devToolsTheme) {
            this.getColorByPrefix = colorByPrefixGetter(this.getColors(opts.devToolsTheme));
        }

        super.applyRuntimeOpts(opts);
    }

    /** @param {LoggerFormattedMessage} message - message formatted by logger */
    log(message) {
        /* eslint-disable no-console */
        const level = console[message.lvl] ? message.lvl : 'log';

        if (!isIE()) {
            console[level](
                `%c[${message.prefix}]\n%c${message.tsz.split('T')[1]}`,
                `color: ${this.getColorByPrefix(message.prefix)}`,
                'color: #788; font-style: italic',
                ...message.parts
            );
        } else {
            console[level](`[${message.prefix}]`, ...message.parts);
        }
        /* eslint-enable no-console */
    }
}

export class NodeConsoleTransport extends ConsoleTransport {
    static colorByLevel = { error: 31, warn: 33, info: 32, debug: 36 };

    colorize(color, str) {
        return `\x1b[${color}m${str}\x1b[0m`;
    }

    /** @param {LoggerFormattedMessage} message - message formatted by logger */
    log(message) {
        // eslint-disable-next-line no-console
        console.log(
            `[${message.prefix}]`,
            this.colorize(
                NodeConsoleTransport.colorByLevel[message.lvl],
                message.lvl.toUpperCase()
            ),
            ...message.parts
        );
    }
}
